import { z } from 'zod';
export const MAX_DOMAINS_PER_LOCAL = 3;
export const HOSTNAME_MAX_LENGTH = 253;
const LABEL_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
/**
 * Sufijos de dos niveles frecuentes. Solo se usan para avisar en el formulario que falta el
 * subdominio; la validación definitiva (con la Public Suffix List) la hace la API.
 */
const COMMON_SECOND_LEVEL_SUFFIXES = new Set([
    'com.ar', 'net.ar', 'org.ar', 'gob.ar', 'gov.ar', 'edu.ar', 'int.ar', 'mil.ar', 'tur.ar',
    'coop.ar', 'mutual.ar', 'musica.ar', 'bet.ar', 'senasa.ar',
    'com.uy', 'com.py', 'com.bo', 'com.br', 'com.mx', 'com.co', 'com.pe', 'com.ec', 'com.ve',
    'com.es', 'co.uk', 'com.au',
]);
/**
 * Limpia lo que pega la gente: `https://Menu.Mi-Local.com.ar/` → `menu.mi-local.com.ar`.
 * Saca protocolo, path, query, puerto y punto final.
 */
export function normalizeHostnameInput(raw) {
    let h = (raw ?? '').trim().toLowerCase();
    h = h.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    h = h.split(/[/?#]/)[0] ?? '';
    const at = h.lastIndexOf('@');
    if (at >= 0)
        h = h.slice(at + 1);
    if (!h.startsWith('['))
        h = h.replace(/:\d*$/, '');
    return h.replace(/\.+$/, '');
}
/** Error de sintaxis del hostname (ya normalizado) o `null` si la forma es válida. */
export function hostnameSyntaxError(hostname) {
    if (!hostname)
        return 'Ingresá un dominio';
    if (hostname.includes('*'))
        return 'No se pueden usar comodines (*): ingresá un subdominio concreto, p. ej. menu.tuheladeria.com.ar';
    if (hostname.length > HOSTNAME_MAX_LENGTH)
        return 'El dominio es demasiado largo';
    const labels = hostname.split('.');
    if (labels.length < 2)
        return 'Ingresá un dominio completo, p. ej. menu.tuheladeria.com.ar';
    if (!labels.every((l) => LABEL_RE.test(l))) {
        return 'El dominio solo puede tener letras, números, guiones y puntos (sin espacios ni acentos)';
    }
    if (/^\d+$/.test(labels[labels.length - 1]))
        return 'Ingresá un dominio, no una dirección IP';
    return null;
}
/** Heurística para el formulario: ¿parece un dominio "pelado" (sin subdominio)? */
export function looksLikeApexDomain(hostname) {
    const labels = hostname.split('.');
    if (labels.length <= 2)
        return true;
    const lastTwo = labels.slice(-2).join('.');
    return labels.length === 3 && COMMON_SECOND_LEVEL_SUFFIXES.has(lastTwo);
}
/**
 * Aproxima qué poner en "Nombre" del registro CNAME: `menu.heladeria.com.ar` →
 * `{ name: 'menu', zone: 'heladeria.com.ar' }`. Es orientativo (sin Public Suffix List).
 */
export function guessDnsRecordName(hostname) {
    const labels = hostname.split('.');
    const lastTwo = labels.slice(-2).join('.');
    const zoneLabels = labels.length > 2 && COMMON_SECOND_LEVEL_SUFFIXES.has(lastTwo) ? 3 : 2;
    if (labels.length <= zoneLabels)
        return { name: '@', zone: hostname };
    return { name: labels.slice(0, -zoneLabels).join('.'), zone: labels.slice(-zoneLabels).join('.') };
}
export const APEX_NOT_ALLOWED_MESSAGE = 'No se puede usar el dominio pelado: tiene que ser un subdominio, p. ej. menu.tuheladeria.com.ar';
/** Hostname para el formulario de "Conectar dominio propio" (normaliza y valida forma + apex). */
export const domainHostnameSchema = z
    .string()
    .transform(normalizeHostnameInput)
    .superRefine((h, ctx) => {
    const err = hostnameSyntaxError(h);
    if (err) {
        ctx.addIssue({ code: 'custom', message: err });
        return;
    }
    if (looksLikeApexDomain(h))
        ctx.addIssue({ code: 'custom', message: APEX_NOT_ALLOWED_MESSAGE });
});
export const addDomainSchema = z.object({ hostname: domainHostnameSchema });
/**
 * Body que acepta la API: solo forma básica. Las reglas de negocio (apex, reservados, sintaxis)
 * las valida el servidor con códigos de error específicos.
 */
export const addDomainRequestSchema = z.object({
    hostname: z.string().trim().min(1, 'Ingresá un dominio').max(HOSTNAME_MAX_LENGTH + 16),
});
//# sourceMappingURL=domains.js.map